import {
  BaseError,
  ContractFunctionRevertedError,
  InsufficientFundsError,
  UserRejectedRequestError,
  decodeErrorResult,
  type Hex,
} from 'viem';
import { protocolErrorsAbi } from '@syntra/abi';
import { en } from '../../i18n/en.ts';
import type { MessageKey } from '../../i18n/index.tsx';
import { fmtBps, fmtDuration, fmtPct, fmtToken } from '../../lib/format.ts';

export type Translate = (key: MessageKey, vars?: Record<string, string | number>) => string;

/** What went wrong, in the user's language, with what to do next; `name` is the protocol error, when it was one. */
export interface ErrorDescription {
  text: string;
  name?: string;
}

const num = (v: unknown, digits = 2) => (typeof v === 'bigint' ? fmtToken(v, digits) : '?');
const date = (v: unknown) => (typeof v === 'bigint' ? new Date(Number(v) * 1000).toLocaleString() : '?');

/** Errors whose arguments say what the limit was, so the user sees the real numbers. */
const ARGS: Record<string, (args: readonly unknown[]) => Record<string, string | number>> = {
  RebaseBacklog: ([n]) => ({ n: String(n) }),
  FeeBelowRate: ([plan, feeBps, rate]) => ({ plan: Number(plan), fee: fmtBps(Number(feeBps)), rate: fmtPct(Number(rate) / 1e18, 3) }),
  LotLocked: ([, unlockAt]) => ({ date: date(unlockAt) }),
  InsufficientStake: ([, available]) => ({ available: num(available, 4) }),
  PermanentPrincipal: ([, available]) => ({ available: num(available, 4) }),
  CapacityExceeded: ([payout, left]) => ({ payout: num(payout), left: num(left) }),
  MaxPayoutExceeded: ([payout, max]) => ({ payout: num(payout), max: num(max) }),
  SlippageExceeded: ([payout, min]) => ({ payout: num(payout, 4), min: num(min, 4) }),
  StillVesting: ([, unlockAt]) => ({ date: date(unlockAt) }),
  PriceDeviation: ([spot, twap, maxBps]) => {
    const dev = typeof spot === 'bigint' && typeof twap === 'bigint' && twap > 0n ? (Math.abs(Number(spot - twap)) / Number(twap)) * 100 : NaN;
    return { spot: num(spot, 4), twap: num(twap, 4), dev: dev.toFixed(2), max: Number(maxBps) / 100 };
  },
  StalePrice: ([, updatedAt]) => ({
    age: typeof updatedAt === 'bigint' && updatedAt > 0n ? fmtDuration(Math.max(0, Math.floor(Date.now() / 1000) - Number(updatedAt))) : '?',
  }),
  StablecoinDepeg: ([price]) => ({ price: num(price, 4) }),
  MinterBudgetExceeded: ([, , available]) => ({ available: num(available) }),
  GlobalBudgetExceeded: ([, available]) => ({ available: num(available) }),
  ERC20InsufficientBalance: ([, have, needed]) => ({ have: num(have, 4), needed: num(needed, 4) }),
  StillLocked: ([unlockAt]) => ({ date: date(unlockAt) }),
  CooldownActive: ([nextAt]) => ({ date: date(nextAt) }),
};

/** Errors a user cannot act on individually share one sentence per family, named in the text. */
const GROUP: Record<string, MessageKey> = {
  InvalidConfig: 'err.group.setting',
  InvalidAddress: 'err.group.setting',
  InvalidDuration: 'err.group.setting',
  RateTooHigh: 'err.group.setting',
  FeeTooHigh: 'err.group.setting',
  FeeBelowRate: 'err.group.setting',
  TokenAlreadySet: 'err.group.setting',
  TokenNotSet: 'err.group.setting',
  StakingAlreadySet: 'err.group.setting',
  OnlyDeployer: 'err.group.setting',
  OnlyMinterRegistry: 'err.group.setting',
  AccessControlBadConfirmation: 'err.group.setting',
  DelayBelowFloor: 'err.group.timelock',
  InvalidSafe: 'err.group.timelock',
  TimelockInsufficientDelay: 'err.group.timelock',
  TimelockInvalidOperationLength: 'err.group.timelock',
  TimelockUnexecutedPredecessor: 'err.group.timelock',
  ERC20InvalidApprover: 'err.group.token',
  ERC20InvalidReceiver: 'err.group.token',
  ERC20InvalidSender: 'err.group.token',
  ERC20InvalidSpender: 'err.group.token',
  SafeERC20FailedOperation: 'err.group.token',
  ReentrancyGuardReentrantCall: 'err.group.internal',
  FailedCall: 'err.group.internal',
  AddressEmptyCode: 'err.group.internal',
  NativeTransferFailed: 'err.group.internal',
  ExpectedPause: 'err.group.internal',
  NothingToRelease: 'err.group.lock',
  NotBeneficiary: 'err.group.lock',
  NotLater: 'err.group.lock',
  TooLong: 'err.group.lock',
  LockedToken: 'err.group.lock',
  NotTriggered: 'err.group.buyback',
  TradeTooLarge: 'err.group.buyback',
  DailyBudgetExceeded: 'err.group.buyback',
  ERC1967InvalidImplementation: 'err.group.upgrade',
  ERC1967NonPayable: 'err.group.upgrade',
  UUPSUnauthorizedCallContext: 'err.group.upgrade',
  UUPSUnsupportedProxiableUUID: 'err.group.upgrade',
  InvalidInitialization: 'err.group.upgrade',
  NotInitializing: 'err.group.upgrade',
  ECDSAInvalidSignature: 'err.group.signature',
  ECDSAInvalidSignatureLength: 'err.group.signature',
  ECDSAInvalidSignatureS: 'err.group.signature',
  ERC2612ExpiredSignature: 'err.group.signature',
  ERC2612InvalidSigner: 'err.group.signature',
  InvalidAccountNonce: 'err.group.signature',
  InvalidShortString: 'err.group.signature',
  StringTooLong: 'err.group.signature',
};

/** EIP-1193 / EIP-1474 provider codes that wallets return. */
const WALLET_CODES = new Set([4001, 4100, 4200, 4900, 4901, 4902, -32002, -32603]);

/** Node and wallet wordings for a sender that cannot pay gas (gas estimation runs out at a zero balance). */
const NO_GAS_TEXT = /insufficient funds|gas required exceeds allowance \(0\)|exceeds balance/i;

/** A protocol error in words: its own sentence when the catalogue has one, else its family's, else a generic one. */
function protocolText(name: string, args: readonly unknown[], t: Translate): string {
  const vars = ARGS[name]?.(args) ?? {};
  const own = `err.${name}`;
  if (own in en) return t(own as MessageKey, vars);
  const group = GROUP[name];
  return group ? t(group, { name, ...vars }) : t('err.unknown', { name });
}

export function describeError(e: unknown, t: Translate): ErrorDescription {
  if (e instanceof BaseError) {
    if (e.walk((x) => x instanceof UserRejectedRequestError)) return { text: t('err.wallet.4001') };
    if (e.walk((x) => x instanceof InsufficientFundsError)) return { text: t('err.noGas') };
    const revert = e.walk((x) => x instanceof ContractFunctionRevertedError) as ContractFunctionRevertedError | null;
    const decoded = revert?.data ?? decodeRaw((revert as { raw?: Hex } | null)?.raw);
    const name = decoded?.errorName;
    if (name) return { text: protocolText(name, decoded.args ?? [], t), name };
    const code = codeOf(e);
    if (code !== undefined && WALLET_CODES.has(code)) return { text: t(`err.wallet.${code}` as MessageKey) };
    if (NO_GAS_TEXT.test(messageOf(e.walk()) ?? '') || NO_GAS_TEXT.test(e.message)) return { text: t('err.noGas') };
    return { text: e.shortMessage };
  }
  // Some wallet extensions reject with a plain object ({ code, message }) or an Error whose message is not a string;
  // String() of those is "[object Object]".
  const code = codeOf(e);
  if (code !== undefined && WALLET_CODES.has(code)) return { text: t(`err.wallet.${code}` as MessageKey) };
  if (NO_GAS_TEXT.test(messageOf(e) ?? '')) return { text: t('err.noGas') };
  return { text: messageOf(e) ?? t('err.walletGeneric') };
}

export function explainError(e: unknown, t: Translate): string {
  return describeError(e, t).text;
}

/** The provider error code, from the error itself or the wallet error it wraps (`cause`, `error`, `data`). */
function codeOf(e: unknown, depth = 0): number | undefined {
  if (!e || typeof e !== 'object' || depth > 4) return undefined;
  const o = e as { code?: unknown; cause?: unknown; error?: unknown; data?: unknown };
  if (typeof o.code === 'number') return o.code;
  return codeOf(o.cause, depth + 1) ?? codeOf(o.error, depth + 1) ?? codeOf(o.data, depth + 1);
}

/** The first readable message on the error or anything it wraps. */
function messageOf(e: unknown, depth = 0): string | undefined {
  if (typeof e === 'string') return e.trim() || undefined;
  if (!e || typeof e !== 'object' || depth > 4) return undefined;
  const o = e as { shortMessage?: unknown; message?: unknown; reason?: unknown; cause?: unknown; error?: unknown; data?: unknown };
  for (const m of [o.shortMessage, o.message, o.reason]) if (typeof m === 'string' && m.trim() && m !== '[object Object]') return m.trim();
  return messageOf(o.message, depth + 1) ?? messageOf(o.cause, depth + 1) ?? messageOf(o.error, depth + 1) ?? messageOf(o.data, depth + 1);
}

function decodeRaw(raw: Hex | undefined): { errorName: string; args?: readonly unknown[] } | undefined {
  if (!raw || raw === '0x') return undefined;
  try {
    return decodeErrorResult({ abi: protocolErrorsAbi, data: raw });
  } catch {
    return undefined;
  }
}
